import { Suspense } from "react";
import { lazyWithReload } from "./lib/lazyWithReload";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./auth/AuthProvider";
import { useAuth } from "./auth/useAuth";
import Splash from "./pages/Splash";
import AuthPage from "./pages/AuthPage";
import TourProvider from "./onboarding/TourProvider";
import UpdatePrompt from "./components/UpdatePrompt";
import AgeGate from "./components/AgeGate";
import "./styles/site.css";

// Code-split the heavy authed shell (trainer + all tabs) and the transactional
// pages off the signed-out splash/login bundle (perf sweep A1).
const AppShell = lazyWithReload(() => import("./pages/AppShell"));
const Account = lazyWithReload(() => import("./pages/Account"));
const ConfirmEmail = lazyWithReload(() => import("./pages/ConfirmEmail"));
const ResetPassword = lazyWithReload(() => import("./pages/ResetPassword"));

function Loading() {
  return (
    <div className="site center">
      <p className="muted">Loading…</p>
    </div>
  );
}

/* signed-in users only */
function RequireAuth({ children }) {
  const { session } = useAuth();
  if (session === undefined) return <Loading />;
  if (!session) return <Navigate to="/" replace />;
  return children;
}

/* signed-out users only (splash, login, signup) */
function PublicOnly({ children }) {
  const { session } = useAuth();
  if (session === undefined) return <Loading />;
  if (session) return <Navigate to="/app" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={<PublicOnly><Splash /></PublicOnly>} />
          <Route path="/login" element={<PublicOnly><AuthPage mode="login" /></PublicOnly>} />
          <Route path="/signup" element={<PublicOnly><AuthPage mode="signup" /></PublicOnly>} />
          {/* AppShell is a layout that stays mounted across /app and
              /account, so the Practice trainer (and the running count) is
              never torn down — even mid-hand — when visiting account. */}
          <Route
            element={
              <TourProvider>
                <RequireAuth>
                  <AgeGate>
                    <AppShell />
                  </AgeGate>
                </RequireAuth>
              </TourProvider>
            }
          >
            <Route path="app" element={null} />
            <Route path="account" element={<Account />} />
          </Route>
          {/* no guard: the recovery link sets a session, and the user must
              reach this page anyway to choose a new password */}
          <Route path="/reset" element={<ResetPassword />} />
          {/* no guard: confirms the email + signs the user in, then redirects */}
          <Route path="/confirm" element={<ConfirmEmail />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      <UpdatePrompt />
    </AuthProvider>
  );
}
